import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled, { css } from 'styled-components';

import { EditionVariant, Skin } from '#src/__generated__/globalTypes.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';

import type { SkinEditionChips_skinEdition } from './__generated__/index.graphql.ts';

const BaseChip = styled.div`
  border-radius: 16px;
  min-height: var(--chip-small-height);
  padding: 2px var(--unit);
  font: var(--t-label-s);
  font-weight: var(--t-bold);
`;
const SkinChip = styled(BaseChip)<{ $skin: Skin }>`
  ${({ $skin }) => {
    switch ($skin) {
      case Skin.SHINY:
        return css`
          background: var(--c-gradient-score-max);
          color: var(--c-black);
          box-shadow: 0 0 20px #df88ff;
        `;
      case Skin.HOLO:
        return css`
          background: var(--c-gradient-hotstreak);
          color: var(--c-black);
          box-shadow: 0 0 20px #fff188;
        `;
      case Skin.FULL_ART:
        return css`
          background: var(--c-gradient-gold);
          color: var(--c-black);
          box-shadow: 0 0 20px #ff9c88;
        `;
      case Skin.STANDARD:
      default:
        return css`
          background: var(--c-nd-50);
          color: var(--c-nd-600);
          backdrop-filter: blur(16px);
        `;
    }
  }}
`;

const VariantChip = styled(BaseChip)`
  &.jersey-number {
    background: var(--c-vault-superRare-progressbar);
    color: var(--c-white);
    box-shadow: 0 0 20px #88b6ff;
  }
  &.meteor-striker {
    background: var(--c-gradient-bronze);
    color: var(--c-black);
    box-shadow: 0 0 20px #ff9c88;
  }
  &.legendary {
    background: var(--c-gradient-mix);
    color: white;
    box-shadow: 0 0 20px #ef88ff;
  }
  &.action-shot {
    background: var(--c-gradient-conversionCreditBorder);
    color: var(--c-black);
    box-shadow: 0 0 20px #fff188;
  }
`;

type Props = {
  skinEdition: Nullable<SkinEditionChips_skinEdition>;
};

export const SkinEditionChips = ({ skinEdition }: Props) => {
  if (!skinEdition) {
    return null;
  }
  const { skin, variant, skinName, variantName } = skinEdition;

  const getVariantName = () => {
    if (skin === Skin.SHINY) {
      switch (variant) {
        case EditionVariant.VARIANT_1:
          return 'jersey-number';
        case EditionVariant.VARIANT_2:
          return 'meteor-striker';
        default:
          return '';
      }
    }
    if (skin === Skin.FULL_ART) {
      switch (variant) {
        case EditionVariant.VARIANT_1:
          return 'legendary';
        default:
          return '';
      }
    }
    if (skin === Skin.HOLO) {
      switch (variant) {
        case EditionVariant.VARIANT_1:
          return 'action-shot';
        default:
          return '';
      }
    }
    return '';
  };

  return (
    <Horizontal>
      <SkinChip $skin={skin}>{skinName}</SkinChip>
      {!!variant && variant !== EditionVariant.BASE && (
        <VariantChip className={getVariantName()}>{variantName}</VariantChip>
      )}
    </Horizontal>
  );
};

SkinEditionChips.fragments = {
  skinEdition: gql`
    fragment SkinEditionChips_skinEdition on SkinEdition {
      skin
      skinName
      variant
      variantName
    }
  ` as TypedDocumentNode<SkinEditionChips_skinEdition>,
};
